/* =====================================================================
   Sheodra Wishlist — storage module (v1)
   ---------------------------------------------------------------------
   THE single source of truth for the wishlist. Nothing else in the theme
   is allowed to touch localStorage directly — hearts, the header counter
   and the wishlist page all talk ONLY to window.SheodraWishlist.

   That boundary is the whole point: if we ever move to a real backend
   (app proxy + server), only the internals of read()/write() change.
   No button, no section and no CSS has to be touched.

   Stored item shape:  { h: "product-handle", v: "variantId" | null }
   We deliberately store nothing else. Price / title / image are fetched
   fresh when the wishlist renders, so a saved item can never show a stale
   price — jewellery prices move, and a wrong price is worse than no list.
   ===================================================================== */
(function (w) {
  'use strict';

  /* This file may end up included twice (header + a section). Running it again would
     be harmless for state (that lives in localStorage) but it would hand out a brand
     new object, so just bail out if we already exist. */
  if (w.SheodraWishlist) return;

  var KEY = 'sheodra_wishlist_v1';
  var EVT = 'wishlist:change';
  var MAX = 50; /* keeps the share URL well inside safe URL length */

  /* Some browsers (incognito, storage disabled, quota full) throw on write.
     Rather than letting the heart silently do nothing, we fall back to an
     in-memory list so the wishlist still works for that session. */
  var canStore = (function () {
    try {
      localStorage.setItem('__sh_t', '1');
      localStorage.removeItem('__sh_t');
      return true;
    } catch (e) {
      return false;
    }
  })();
  var mem = [];

  /* ---------------------------------------------------------------- */

  function keyOf(item) {
    return item.h + ':' + (item.v == null ? '' : item.v);
  }

  function normalize(it) {
    if (!it || typeof it.h !== 'string' || !it.h) return null;
    return { h: it.h, v: it.v == null || it.v === '' ? null : String(it.v) };
  }

  function read() {
    if (!canStore) return mem.slice();
    try {
      var arr = JSON.parse(localStorage.getItem(KEY) || '[]');
      if (!Array.isArray(arr)) return [];
      var out = [];
      for (var i = 0; i < arr.length; i++) {
        var n = normalize(arr[i]);
        if (n) out.push(n);
      }
      return out;
    } catch (e) {
      return [];
    }
  }

  function write(arr) {
    var capped = arr.slice(0, MAX);
    if (canStore) {
      try {
        localStorage.setItem(KEY, JSON.stringify(capped));
      } catch (e) {
        /* quota blew up mid-session — degrade to memory rather than break */
        canStore = false;
        mem = capped;
      }
    } else {
      mem = capped;
    }
    emit();
  }

  function emit() {
    var detail = { count: count(), items: all() };
    var ev;
    try {
      ev = new CustomEvent(EVT, { detail: detail });
    } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(EVT, false, false, detail);
    }
    w.dispatchEvent(ev);
  }

  /* ---- public read API -------------------------------------------- */

  function all() {
    return read();
  }

  function count() {
    return read().length;
  }

  function has(k) {
    var arr = read();
    for (var i = 0; i < arr.length; i++) {
      if (keyOf(arr[i]) === k) return true;
    }
    return false;
  }

  /* PRODUCT-level check, for hearts on cards. On a card the customer has not picked a
     metal/size, so the card heart must mean "this piece is in my list" — in ANY variant.
     Without this, saving the ring in gold from the product page would leave the card heart
     looking empty, and clicking it would add the same ring a SECOND time. */
  function hasProduct(handle) {
    var arr = read();
    for (var i = 0; i < arr.length; i++) {
      if (arr[i].h === handle) return true;
    }
    return false;
  }

  /* Un-hearting a card removes the piece entirely — every variant of it. */
  function removeProduct(handle) {
    var arr = read();
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      if (arr[i].h !== handle) out.push(arr[i]);
    }
    if (out.length === arr.length) return false;
    write(out);
    return true;
  }

  /* ---- public write API ------------------------------------------- */

  function add(item) {
    var n = normalize(item);
    if (!n) return false;
    var arr = read();
    var k = keyOf(n);
    for (var i = 0; i < arr.length; i++) {
      if (keyOf(arr[i]) === k) return false; /* already saved */
    }
    arr.unshift(n); /* newest first */
    write(arr);
    return true;
  }

  function remove(k) {
    var arr = read();
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      if (keyOf(arr[i]) !== k) out.push(arr[i]);
    }
    if (out.length === arr.length) return false; /* nothing removed */
    write(out);
    return true;
  }

  /* Returns the NEW saved-state: true = now saved, false = now removed. */
  function toggle(item) {
    var n = normalize(item);
    if (!n) return false;
    var k = keyOf(n);
    if (has(k)) {
      remove(k);
      return false;
    }
    add(n);
    return true;
  }

  function clear() {
    write([]);
  }

  /* ---- share link: the list travels inside the URL ------------------
     Format:  handle:variantId,handle,handle:variantId
     This is what makes "send my list to my partner" AND "open my list on
     another device" work without any server.                            */

  function encode(arr) {
    var list = arr || read();
    var parts = [];
    for (var i = 0; i < list.length; i++) {
      parts.push(list[i].v ? list[i].h + ':' + list[i].v : list[i].h);
    }
    return parts.join(',');
  }

  function decode(str) {
    if (!str) return [];
    var raw = String(str).split(',');
    var out = [];
    for (var i = 0; i < raw.length && out.length < MAX; i++) {
      var bits = raw[i].split(':');
      var n = normalize({ h: (bits[0] || '').trim(), v: bits[1] ? bits[1].trim() : null });
      if (n) out.push(n);
    }
    return out;
  }

  function shareUrl(origin) {
    var base = (origin || w.location.origin) + '/pages/wishlist';
    var q = encode();
    return q ? base + '?w=' + encodeURIComponent(q) : base;
  }

  /* ---------------------------------------------------------------- */

  w.SheodraWishlist = {
    /* read */
    all: all,
    count: count,
    has: has,
    hasProduct: hasProduct,
    keyOf: keyOf,
    /* write */
    add: add,
    remove: remove,
    removeProduct: removeProduct,
    toggle: toggle,
    clear: clear,
    /* sharing */
    encode: encode,
    decode: decode,
    shareUrl: shareUrl,
    /* constants — the boot script and the CSS rely on these */
    EVENT: EVT,
    STORAGE_KEY: KEY,
    MAX: MAX,
    persistent: canStore
  };
})(window);
